import React from 'react';
import {
  Box, Button, Menu, MenuItem, useTheme,
} from '@mui/material';
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import moment from 'moment';

interface OwnProps {
  readonly timeframe: string;
  readonly SetTimeFrame: (newTimeFrame: string) => void;
  readonly overrideTimeframes?: Timeframe[];
}

type Props = OwnProps;

export enum Timeframe {
  // Current Timeframes
  // These operate as they would on a calendar.
  // Example: Today is Wednesday. WEEK starts on Sunday and ends on Saturday
  TODAY = 'today',
  TOMORROW = 'tomorrow',
  YESTERDAY = 'yesterday',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
  ALL = 'all',
  // Past Timeframes
  // These operate as an "up to date" window. They start as they would on a calendar but stop on the current day.
  // Example: Today is a Wednesday. PAST_WEEK Starts on Sunday and ends on Wednesday.
  PAST_WEEK = 'pastWeek',
  PAST_MONTH = 'pastMonth',
  PAST_YEAR = 'pastYear',
  PAST_ALL = 'pastAll',
  // Future Timeframes
  // These operate as an "from here on" window. These start on the current day and end as they would on a calendar.
  // Example: Today is Wednesday. FUTURE_WEEK starts on Wednesday and ends on Saturday.
  FUTURE_WEEK = 'futureWeek',
  FUTURE_MONTH = 'futureMonth',
  FUTURE_YEAR = 'futureYear',
  FUTURE_ALL = 'futureAll',
  // Past Window Timeframes
  // These ignore calendar structures and operate on a fixed number of days. These start on the specified interval and end on the current day.
  // Example: Today is a Wednesday. PAST_7_DAYS starts on the previous Wednesday and ends on Wednesday.
  PAST_7_DAYS = 'past7',
  PAST_30_DAYS = 'past30',
  PAST_365_DAYS = 'past365',
  // Future Window Timeframes
  // These ignore calendar structures and operate on a fixed number of days. These start on the current day and end on the specified interval.
  // Example: Today is a Wednesday. FUTURE_7_DAYS starts on Wednesday and ends on the next Wednesday.
  FUTURE_7_DAYS = 'future7',
  FUTURE_30_DAYS = 'future30',
  FUTURE_365_DAYS = 'future365',
  //Other
  SEASON_LONG = 'season',
  PREVIOUS_SEASON = 'previousSeason',
}

export const getISOStringsForTimeframe = (timeframe: Timeframe): {startISOString: string | undefined, endISOString: string | undefined} | undefined => {
  switch(timeframe) {
    case Timeframe.TODAY:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().endOf('day').toISOString()
      };
    case Timeframe.TOMORROW:
      return {
        startISOString: moment().add(1, 'day').startOf('day').toISOString(),
        endISOString:moment().add(1, 'day').endOf('day').toISOString()
      };
    case Timeframe.YESTERDAY:
    return {
        startISOString: moment().subtract(1, 'day').startOf('day').toISOString(),
        endISOString:moment().subtract(1, 'day').endOf('day').toISOString()
      };
    case Timeframe.WEEK:
      return {
        startISOString: moment().startOf('week').toISOString(),
        endISOString: moment().endOf('week').toISOString()
      };
    case Timeframe.MONTH:
      return {
        startISOString: moment().startOf('month').toISOString(),
        endISOString: moment().endOf('month').toISOString()
      };
    case Timeframe.YEAR:
      return {
        startISOString: moment().startOf('year').toISOString(),
        endISOString: moment().endOf('year').toISOString()
      };
    case Timeframe.ALL:
      return undefined;
    case Timeframe.PAST_WEEK:
      return {
        startISOString: moment().startOf('week').toISOString(),
        endISOString: moment().endOf('day').toISOString()
      };
    case Timeframe.PAST_MONTH:
      return {
        startISOString: moment().startOf('month').toISOString(),
        endISOString: moment().endOf('day').toISOString()
      };
    case Timeframe.PAST_YEAR:
      return {
        startISOString: moment().startOf('year').toISOString(),
        endISOString: moment().endOf('day').toISOString()
      };
    case Timeframe.PAST_ALL:
      return {
        startISOString: undefined,
        endISOString: moment().endOf('day').toISOString()
      };
    case Timeframe.FUTURE_WEEK:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().endOf('week').toISOString()
      };
    case Timeframe.FUTURE_MONTH:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().endOf('month').toISOString()
      };
    case Timeframe.FUTURE_YEAR:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().endOf('year').toISOString()
      };
    case Timeframe.FUTURE_ALL:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: undefined
      };    
    case Timeframe.PAST_7_DAYS:
      return {
        startISOString: moment().subtract(7, 'days').startOf('day').toISOString(),
        endISOString: moment().startOf('day').toISOString()
        };
    case Timeframe.PAST_30_DAYS:
      return {
        startISOString: moment().subtract(30, 'days').startOf('day').toISOString(),
        endISOString: moment().startOf('day').toISOString()
        };
    case Timeframe.PAST_365_DAYS:
      return {
        startISOString: moment().subtract(365, 'days').startOf('day').toISOString(),
        endISOString: moment().startOf('day').toISOString()
        };
    case Timeframe.FUTURE_7_DAYS:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().add(7, 'days').endOf('day').toISOString()
      };
    case Timeframe.FUTURE_30_DAYS:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().add(30,'days').startOf('day').toISOString(),
      };
    case Timeframe.FUTURE_365_DAYS:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().add(365,'days').startOf('day').toISOString(),
      };
    case Timeframe.SEASON_LONG: 
    //Intentional fall through. Returning no dates for this case was the existing logic.
    case Timeframe.PREVIOUS_SEASON: 
    //Intentional fall through. Returning no dates for this case was the existing logic.
    default:
      return undefined;
  };
};

export const getDisplayTextForTimeframe = (timeframe: Timeframe) => {
  switch(timeframe) {
    case Timeframe.TODAY:
      return 'Today';
    case Timeframe.TOMORROW:
      return 'Tomorrow';
    case Timeframe.YESTERDAY:
      return 'Yesterday';
    case Timeframe.PAST_WEEK:
    case Timeframe.FUTURE_WEEK:
    case Timeframe.WEEK:
      return 'This Week';
    case Timeframe.PAST_MONTH:
    case Timeframe.FUTURE_MONTH:
    case Timeframe.MONTH:
      return 'This Month';
    case Timeframe.PAST_YEAR:
    case Timeframe.FUTURE_YEAR:
    case Timeframe.YEAR:
      return 'This Year';
    case Timeframe.PAST_ALL:
    case Timeframe.FUTURE_ALL:
    case Timeframe.ALL:
      return 'All Days';
    case Timeframe.PAST_7_DAYS:
      return 'Last 7 Days';
    case Timeframe.PAST_30_DAYS:
      return 'Last 30 Days';
    case Timeframe.PAST_365_DAYS:
      return 'Last 365 Days';
    case Timeframe.FUTURE_7_DAYS:
      return 'Next 7 Days';
    case Timeframe.FUTURE_30_DAYS:
      return 'Next 30 Days';
    case Timeframe.FUTURE_365_DAYS:
      return 'Next 365 Days';
    case Timeframe.SEASON_LONG:
      return 'Current Season';
    case Timeframe.PREVIOUS_SEASON:
      return 'Previous Season';
    default:
      return 'All Days';
  };
};

const TimeFrameDropdown = (props: Props) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'timeframe-multi-select',
  });
  const {
    timeframe,
    SetTimeFrame,
    overrideTimeframes,
  } = props;

  const defaultTimeframes: Timeframe[] = [Timeframe.FUTURE_7_DAYS, Timeframe.WEEK , Timeframe.TODAY, Timeframe.TOMORROW, Timeframe.FUTURE_365_DAYS];
  const _timeframes = overrideTimeframes ? overrideTimeframes : defaultTimeframes;
  const theme = useTheme();

  const handleChange = (newValue: string) => {
    SetTimeFrame(newValue);
    popupState.setOpen(false);
  };

  return (
    <Box id='timeframe-toggle' data-filter="sportsbook" sx={{
      width: '100%',
      maxWidth: '100%',
    }}>
      <Button
        sx={{
          height: 40,
          minWidth: 125,
          width: '100%',
          padding: '4px 16px !important',
          maxWidth: '100% !important',
          whiteSpace: 'nowrap',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
          },
          justifyContent: 'space-between !important',
          textTransform: 'capitalize !important',
          backgroundColor: '#1C1C1C !important',
          border: `0px`,
          color: theme.palette.common.white,
          fontWeight: 600,
          fontSize: 13,
          '&.MuiButton-root': {
            padding: '4px 16px !important',
          },
          '&.MuiButton-outlinedPrimary': {
            color: theme.palette.common.white,
            padding: '4px 16px !important',
          },
          '& .MuiSvgIcon-root': {
            color: theme.palette.common.white,
          },
          '&.selected': {
            backgroundColor: theme.palette.grey[700],
            color: theme.palette.getContrastText(theme.palette.grey[700]),
            fontWeight: 500,
          },
        }}
        aria-controls="button-menu"
        aria-haspopup="true"
        variant="contained"
        color="primary"
        endIcon={<ArrowDropDownIcon color="secondary" />}
        {...bindTrigger(popupState)}
      >
        {getDisplayTextForTimeframe(timeframe as Timeframe)}
      </Button>

      <Menu sx={{
        width: '100%',
        '& .MuiPaper-root': {
          maxWidth: '100%',
          border: '1px solid #d3d4d5',
        },
        '& .row': {
          display: 'flex',
          justifyContent: 'flex-start !important',
          flexDirection: 'row',
          alignItems: 'center',
          '& .team': {
            display: 'flex',
            justifyContent: 'flex-start !important',
            flexDirection: 'row',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            [theme.breakpoints.down('sm')]: {
              width: '100%',
              whiteSpace: 'inherit',
            },
            '& .img-wrapper': {
              width: 20,
              height: 20,
              marginRight: 6,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 2,
              '& img': {
                height: '100%',
                width: '100%',
                objectFit: 'contain',
              },
            },
            '& p': {
              fontWeight: 500,
            },
          },
        },
      }} {...bindMenu({ ...popupState })}>
        {_timeframes.map((timeframeOption) => (
          <MenuItem
            key={timeframeOption}
            sx={{
              width: '100%',
              fontWeight: 500,
              fontSize: '0.875rem',
              justifyContent: 'flex-start !important',
              padding: '4px !important',
              '& .MuiListItemText-dense': {
                marginTop: 2,
                marginBottom: 2,
              },
            }}
            dense
            onClick={() => handleChange(timeframeOption)}
          >
            {getDisplayTextForTimeframe(timeframeOption)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default TimeFrameDropdown;